import queryString from 'querystring';
import { addFilterToUrl } from '@prisjakt/product-collection-urls';

const hasQueryString = url =>
  typeof url === 'string' && url.indexOf('?') !== -1;

const removeQueryString = url =>
  hasQueryString(url) ? url.substring(0, url.indexOf('?')) : url;

const queryStringFromUrl = url =>
  url ? url.substr(url.indexOf('?')).substr(1) : '';

const addQueryToUrl = (url, qs, routeSection) => {
  const baseUrl = hasQueryString(url) ? removeQueryString(url) : url;

  return qs
    ? `${baseUrl}${routeSection ? `/${routeSection}` : ''}?${qs}`
    : `${baseUrl}${routeSection ? `/${routeSection}` : ''}`;
};

const getQueryString = (url, query) => {
  // Combine parameters from URL with query object
  const combinedQuery = Object.assign(
    queryString.decode(queryStringFromUrl(url)),
    query
  );
  // Sort object keys to ensure constant parameter order
  const sortedQuery = Object.keys(combinedQuery)
    .sort()
    .reduce((q, key) => ({ ...q, [key]: combinedQuery[key] }), {});
  return queryString.encode(sortedQuery);
};

export const withQueryString = (url, query, routeSection) =>
  addQueryToUrl(url, getQueryString(url, query), routeSection);

function productSectionToClassicIdParam(section = 'main') {
  switch (section) {
    case 'properties':
      return 'e';
    case 'reviews':
      return 'o';
    case 'statistics':
      return 'pu';
    default:
      return 'p';
  }
}

const linkTo = (
  useClassicUrls,
  useHashForProductTabs,
  classicRoutes,
  routes,
  type,
  item = null,
  query = {},
  routeSection = null
) => {
  // Support both graphql schema and api schema
  const semanticId = item && (item.pathName || item.semantic_id);
  const externalUri = item && (item.externalUri || item.redirect_uri);

  function linkToProduct(section) {
    const classicIdParam = useHashForProductTabs
      ? 'p'
      : productSectionToClassicIdParam(section);

    const path = useClassicUrls
      ? withQueryString(classicRoutes.product, {
          ...query,
          [classicIdParam]: item.id,
        })
      : `${withQueryString(semanticId, query)}${
          !useHashForProductTabs && section ? `/${section}` : ''
        }`;

    return useHashForProductTabs && section ? `${path}#${section}` : path;
  }

  switch (type) {
    case 'raw': {
      return externalUri;
    }
    case 'product':
    case 'raw_sorted': {
      return linkToProduct();
    }
    case 'productProperties': {
      return linkToProduct('properties');
    }
    case 'productReviews': {
      return linkToProduct('reviews');
    }
    case 'productPriceHistory': {
      return linkToProduct('statistics');
    }
    case 'productAlert': {
      return useClassicUrls
        ? withQueryString(
            classicRoutes.product + routes.productAlert.relativePath,
            { ...query, p: item.id }
          )
        : withQueryString(semanticId + routes.productAlert.relativePath, query);
    }
    case 'productCompare': {
      const { category, products } = item;
      const categoryId = category && category.id;

      const productIds = Array.isArray(products)
        ? products.map(p => p.id).join(',')
        : '';

      return withQueryString(
        routes.productCompare.path.replace(':productIds', productIds),
        categoryId ? { categoryId } : {}
      );
    }
    case 'brand': {
      return useClassicUrls
        ? withQueryString(classicRoutes.brand, { ...query, t: item.id })
        : withQueryString(semanticId, query);
    }

    case 'store': {
      return useClassicUrls
        ? withQueryString(
            classicRoutes.store_info,
            { ...query, f: item.id },
            routeSection
          )
        : withQueryString(semanticId, query, routeSection);
    }

    case 'brandCategory':
    case 'brand_category':
    case 'brand_virtual_category':
      if (item.product_collection || item.productCollection) {
        // eslint-disable-next-line no-param-reassign
        query.brand = item.brandId;
      } else if (useClassicUrls) {
        // eslint-disable-next-line no-param-reassign
        query.t = item.brandId;
      } else {
        // eslint-disable-next-line no-param-reassign
        query.brand = item.brandId;
      }
    case 'virtual_category': // eslint-disable-line no-fallthrough
    case 'virtualCategory':
    case 'category': {
      if (item.product_collection || item.productCollection) {
        return withQueryString(
          item.product_collection || item.productCollection,
          query
        );
      }

      return useClassicUrls
        ? withQueryString(classicRoutes.category, { ...query, k: item.id })
        : withQueryString(semanticId, query);
    }
    case 'shopOfTheYear': {
      return routes.shopOfTheYear.path;
    }
    case 'shopOfTheYearCategory': {
      return routes.shopOfTheYearCategory.path
        .replace(':year', item.year)
        .replace(':id', item.id);
    }
    case 'shopOfTheYearWinners': {
      return `${routes.shopOfTheYearWinners.path.replace(
        ':section',
        item.year
      )}#winners`;
    }
    case 'giftGuide': {
      return `${routes.themes.path}/${item.slug}`;
    }
    case 'thinCategory':
    case 'productCollection': {
      const { filters = [] } = item;

      return filters.reduce(addFilterToUrl, semanticId);
    }
    case 'productCollectionSitemap': {
      const { baseUrl } = item;
      return removeQueryString(baseUrl.replace('/c/', '/cm/'));
    }
    case 'editorialContentListing': {
      return routes.editorialContentListing.path;
    }
    case 'user': {
      return `/user/${item.id}`;
    }
    case 'list': {
      return withQueryString(semanticId, query);
    }
    case 'categoryIndex': {
      return routes.categoryIndex.path;
    }
    case 'expertTest': {
      return withQueryString(semanticId, query);
    }
    case 'expertToplist': {
      return withQueryString(semanticId, query);
    }
    case 'expertCategory': {
      return withQueryString(semanticId, query);
    }
    case 'expertProfile': {
      return withQueryString(semanticId, query);
    }
    case 'expertContent': {
      return withQueryString(semanticId, query);
    }
    case 'expertMedia': {
      return withQueryString(semanticId, query);
    }
    case 'expertArticle': {
      return withQueryString(semanticId, query);
    }
    case 'experts': {
      return withQueryString(routes.expertLandingPage.path, query);
    }
    default:
      return null;
  }
};
export default linkTo;
